import React, { useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import CloseIconOutlined from "../assets/icons/CloseIconOutlined";
import Copy from "../icons/Copy";
import Tick from "../icons/Tick";
import styled from 'styled-components';
import { convertAnyPipeTablesToHtml } from '../utils/markdownTable';
import { isSafeUrl, sanitizeHtml } from '../utils/sanitizeHtml';

const FORBIDDEN_HTML_TAGS = new Set([
  'meta', 'iframe', 'object', 'embed', 'script', 'base', 'link', 'form', 'svg', 'math', 'applet', 'frame', 'frameset',
]);

const chatSanitizeSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames || []).filter((tag: string) => !FORBIDDEN_HTML_TAGS.has(tag)),
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
    src: ['http', 'https', 'data'],
  },
};

const markdownRehypePlugins = [rehypeRaw, [rehypeSanitize, chatSanitizeSchema]] as any;

const MarkdownContainer = styled.div`
  .table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    background-color: white;
    table-layout: fixed;
    
    th, td {
      padding: 12px 16px;
      border: 1px solid #ddd;
      text-align: left;
      background-color: white;
      color: #000;
    }

    th:first-child,
    td:first-child {
      width: 200px;
      min-width: 200px;
      white-space: normal;
      word-wrap: break-word;
      hyphens: auto;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: calc(100% - 200px);
      min-width: 300px;
    }

    td {
      word-wrap: break-word;
      vertical-align: top;
      overflow: visible;
      line-height: 1.4;
    }

    th, td {
      padding: 12px 16px;
      border: 1px solid #ddd;
    }

    border-radius: 4px;
    box-shadow: 0 0 0 1px #ddd;
  }
  .ant-typography mark{
    background-color: transparent;
  }
  padding: 0.5rem;
`;

const HighlightedText = styled.span`
  background-color: #ffeb3b; // Yellow highlight color
  padding: 2px 4px;
  border-radius: 3px;
`;

const WizrMarkDown = ({ children, noMargin, paddingTop, paddingBottom, style, type, isEditedContent, originalContent }: any) => {
  const [modal, setModal] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});
  const fontSize = '0.875rem';
  const inputFontSize = '0.875rem';

  const toggleModal = (src: any) => {
    setPreviewImage(src);
    setModal(!modal);
  };

  const copyToClipboard = async (text: string, blockId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [blockId]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [blockId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Function to detect if original content was HTML
  const wasOriginalContentHtml = (originalContent: string) => {
    if (!originalContent) return false;
    return /<\s*(p|div|h1|h2|h3|h4|h5|h6|ul|ol|li|img|a)[\s>]/i.test(originalContent);
  };

  // Reusable copy button component
  const CopyButton = ({ text, blockId, language = 'html' }: { text: string; blockId: string; language?: string }) => {
    const isCopied = copiedStates[blockId];

    return (
      <button
        onClick={() => copyToClipboard(text, blockId)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: '#6c757d',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (!isCopied) {
            e.currentTarget.style.backgroundColor = '#e9ecef';
          }
        }}
        onMouseLeave={(e) => {
          if (!isCopied) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {isCopied ? (
          <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tick />
          </div>
        ) : <Copy />}
        {isCopied ? 'Copied' : 'Copy'}
      </button>
    );
  };


  if (modal) {
    document.body.classList.add("active-modal");
  } else {
    document.body.classList.remove("active-modal");
  }

  // const isTableData = (content: string) => {
  //   try {
  //     if (content.startsWith('{')) {
  //       const unescapedContent = content.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  //       const parsedContent = JSON.parse(unescapedContent);
  //       return typeof parsedContent === 'object' && 
  //              parsedContent.content && 
  //              parsedContent.content.includes('|');
  //     }
  //     // Check for pipe-separated format
  //     return content.includes('|') && content.includes('\n');
  //   } catch {
  //     return content.includes('|') && content.includes('\n');
  //   }
  // };

  // Updated formatTableContent function to handle both formats
  const formatTableContent = (content: string) => {
    if (!content) return content;

    try {
      // Handle JSON format
      if (content.startsWith('{')) {
        const unescapedContent = content.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
        const parsedContent = JSON.parse(unescapedContent);
        // Remove both single and double asterisks from content
        content = parsedContent.content.replace(/\*/g, '');
      }

      // Split into lines and filter out empty ones
      let lines = content.split('\n').filter(line => line.trim());

      // Find the header and separator rows
      let headerIdx = lines.findIndex(line => line.includes('|'));
      const separatorRegex = /^\s*\|?-/;
      let sepIdx = lines.findIndex(line => separatorRegex.exec(line));

      // If separator row is missing but header is present, insert it
      if (headerIdx !== -1 && (sepIdx === -1 || sepIdx !== headerIdx + 1)) {
        const headerCols = lines[headerIdx].split('|').filter(cell => cell.trim()).length;
        const sepRow = '|' + Array(headerCols).fill('---').join('|') + '|';
        lines.splice(headerIdx + 1, 0, sepRow);
      }

      // Collect all rows that look like table data (pipes or tabs)
      const tableLines: string[] = [];
      for (let i = headerIdx; i < lines.length; i++) {
        let line = lines[i];
        if (line.includes('|') || line.includes('\t')) {
          // Convert tabs to pipes, remove extra spaces, ensure starts/ends with pipe
          line = line.replace(/\t/g, '|').replace(/\s*\|\s*/g, '|').trim();
          if (!line.startsWith('|')) line = '|' + line;
          if (!line.endsWith('|')) line = line + '|';
          tableLines.push(line);
        } else if (line.split(/\s{2,}/).length > 2) {
          // If line has multiple spaces, treat as columns
          const cells = line.trim().split(/\s{2,}/).map(cell => cell.trim());
          let row = '|' + cells.join('|') + '|';
          tableLines.push(row);
        }
      }

      // Remove duplicate header/separator rows
      const uniqueTableLines = tableLines.filter(
        (row, idx, arr) => arr.indexOf(row) === idx
      );

      return buildHtmlTable(uniqueTableLines);

    } catch (error) {
      console.error('Error formatting table content:', error);
      return content;
    }
  };

  const buildHtmlTable = (uniqueTableLines: string[]) => {
    const unescapeMarkdown = (s: string) => s.replace(/\\([\\`*_\{}\[\]()#+\-.!|<>])/g, '$1');
    let htmlTable = '<table class="table"><thead><tr>';
    const headers = uniqueTableLines[0]
      .split('|')
      .filter(cell => cell.trim())
      .map(cell => unescapeMarkdown(cell.trim().replace(/\*/g, '')));
    const headerColCount = Math.max(headers.length, 1);
    headers.forEach(header => {
      htmlTable += `<th>${header}</th>`;
    });
    htmlTable += '</tr></thead><tbody>';

    const emitRow = (cols: string[]) => {
      const padded = Array.from({ length: headerColCount }, (_, idx) => unescapeMarkdown((cols[idx] || '').trim().replace(/\*/g, '')));
      htmlTable += '<tr>';
      padded.forEach(cell => {
        const cellContent = ['null', '-', ''].includes(cell) ? '' : cell;
        htmlTable += `<td>${cellContent}</td>`;
      });
      htmlTable += '</tr>';
    };

    const splitCodeAndDesc = (val: string): [string, string] | null => {
      const trimmed = unescapeMarkdown(val.trim().replace(/\*/g, ''));
      const m = trimmed.match(/^([A-Za-z]{2,}\d{1,})\s+(\S.*)$/);
      if (m) return [m[1], m[2]];
      return null;
    };

    for (let i = 2; i < uniqueTableLines.length; i++) {
      const cells = uniqueTableLines[i]
        .split('|')
        .filter(cell => cell.trim())
        .map(cell => cell.trim().replace(/\*/g, ''));

      if (headerColCount === 2) {
        if (cells.length === 2) { emitRow(cells); continue; }
        const allPairs = cells.every(c => !!splitCodeAndDesc(c));
        if (allPairs) {
          cells.forEach(c => emitRow(splitCodeAndDesc(c)!));
          continue;
        }
      }
      for (let j = 0; j < cells.length; j += headerColCount) emitRow(cells.slice(j, j + headerColCount));
    }

    htmlTable += '</tbody></table>';
    return htmlTable;
  };

  // Preprocess children to replace ____text____ with highlighted text
  const preprocessChildren = (content: string) => {
    if (!content) return '';

    try {
      // Check if content is a JSON string
      if (typeof content === 'string' && content.startsWith('{')) {
        const parsedContent = JSON.parse(content);

        // If it's our specific JSON format with nested JSON string
        if (parsedContent.content) {
          try {
            // Parse the nested JSON string if it exists
            const nestedContent = JSON.parse(parsedContent.content);
            // Convert the nested object to a formatted string
            return Object.entries(nestedContent)
              .map(([key, value]) => `**${key}**: ${value}`)
              .join('\n\n');
          } catch {
            // If nested content isn't JSON, use it directly
            return parsedContent.content;
          }
        }
      }

      // If not JSON or different format, process normally
      return content.replace(/_h_((?:(?!_h_).)*?)_h_/g, (match) => `<mark>${match.slice(3, -3)}</mark>`);
    } catch (error) {
      console.error('Error processing content:', error);
      return content || '';
    }
  };

  const renderContent = (content: string) => {
    // Early cleanup: remove pipe-only lines to avoid stray '|' above/below tables
    content = content
      .split('\n')
      .filter((l) => !/^\s*\|+\s*$/.test(l))
      .join('\n');

    // Check if content contains HTML code blocks (```html...```)
    const htmlCodeBlockRegex = /```html\s*([\s\S]*?)```/gi;
    const hasHtmlCodeBlocks = htmlCodeBlockRegex.test(content);

    if (hasHtmlCodeBlocks) {
      // Split content by HTML code blocks and render each part appropriately
      const parts = content.split(htmlCodeBlockRegex);
      const matches = content.match(htmlCodeBlockRegex);

      return (
        <>
          {parts.map((part, idx) => {
            if (idx % 2 === 0) {
              // Regular text content - render as markdown
              if (part.trim()) {
                return (
                  <Markdown
                    key={`text-part-${idx}`}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={markdownRehypePlugins}
                    components={getMarkdownComponents()}
                  >
                    {preprocessChildren(part)}
                  </Markdown>
                );
              }
              return null;
            } else {
              // HTML code block - render as code
              const htmlCode = matches?.[Math.floor(idx / 2)]?.replace(/^```html\s*/i, '').replace(/\s*```$/i, '') || '';
              const blockId = `html-code-${idx}`;

              return (
                <div
                  key={blockId}
                  style={{
                    margin: '8px 0',
                    borderRadius: '8px',
                    border: '1px solid #e1e5e9',
                    overflow: 'hidden'
                  }}
                >
                  {/* Header with language and copy button */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      borderBottom: '1px solid #e1e5e9',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}
                  >
                    <span style={{ color: '#6c757d' }}>html</span>
                    <CopyButton text={htmlCode} blockId={blockId} />
                  </div>

                  {/* Code content */}
                  <pre
                    className="wizr-html-code"
                    style={{
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#ffffff',
                      padding: '12px',
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      overflow: 'auto',
                      maxHeight: '400px',
                      margin: 0,
                      border: 'none'
                    }}
                  >
                    {htmlCode}
                  </pre>
                </div>
              );
            }
          })}
        </>
      );
    }

    // Check if entire content looks like HTML (fallback for cases without code blocks)
    const looksLikeHtml = /<\s*(p|div|h1|h2|h3|h4|h5|h6|ul|ol|li|img|a)[\s>]/i.test(content);
    
    // Show as HTML code block if:
    // 1. Content looks like HTML AND it's not edited content, OR
    // 2. Content looks like HTML AND it's edited content BUT the original content was also HTML
    if (looksLikeHtml && (!isEditedContent || (isEditedContent && wasOriginalContentHtml(originalContent)))) {
      return (
        <div
          style={{
            margin: '8px 0',
            borderRadius: '8px',
            border: '1px solid #e1e5e9',
            overflow: 'hidden'
          }}
        >
          {/* Header with language and copy button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e1e5e9',
              fontSize: '0.75rem',
              fontWeight: '500'
            }}
          >
            <span style={{ color: '#6c757d' }}>html</span>
            <CopyButton text={content} blockId="fallback-html" />
          </div>

          {/* Code content */}
          <pre
            className="wizr-html-code"
            style={{
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#ffffff',
              padding: '12px',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              overflow: 'auto',
              maxHeight: '400px',
              margin: 0,
              border: 'none'
            }}
          >
            {content}
          </pre>
        </div>
      );
    }

    // Before regular Markdown rendering, convert any pipe tables to HTML robustly
    const pre = convertAnyPipeTablesToHtml(content);
    // Split content into blocks by two or more newlines
    const blocks = pre.split(/\n{2,}/);

    return (
      <>
        {blocks.map((block, idx) => {
          // Detect markdown table: at least 2 lines, first line has pipes, second line is a separator row
          const lines = block.trim().split('\n');
          const separatorRegex = /^[|\-:\s]+$/;
          const isTableBlock =
            lines.length >= 2 &&
            lines[0].includes('|') &&
            lines[1].includes('-') &&
            separatorRegex.exec(lines[1]);

          if (isTableBlock) {
            return (
              <div
                key={`table-block-${block.substring(0, 20)}-${idx}`}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(formatTableContent(block)) }}
              />
            );
          } else {
            return (
              <Markdown
                key={`text-block-${block.substring(0, 20)}-${idx}`}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={markdownRehypePlugins}
                components={getMarkdownComponents()}
              >
                {preprocessChildren(block)}
              </Markdown>
            );
          }
        })}
      </>
    );
  };

  const renderModal = () => {
    if (!modal) return null;

    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          position: "fixed",
          top: 0,
          left: 0,
          backgroundColor: "rgba(49,49,49,0.8)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <button
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            padding: "10px 15px",
            cursor: "pointer",
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "5px",
            zIndex: 10000,
          }}
          onClick={toggleModal}
        >
          <CloseIconOutlined color={'white'} size={'35'} />
        </button>

        <div
          style={{
            position: "relative",
            backgroundColor: "#f1f1f1",
            padding: "20px",
            borderRadius: "10px",
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 10000,
          }}
        >
          <img
            src={previewImage}
            alt="Preview"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </div>
      </div>
    );
  };

  const getMarkdownComponents = () => ({
    p: (props: any) => getParagraphComponent(props),
    table: ({ children }: any) => getTableComponent(children),
    thead: ({ children }: any) => getTheadComponent(children),
    th: ({ children }: any) => getThComponent(children),
    tr: ({ children }: any) => getTrComponent(children),
    td: ({ children }: any) => getTdComponent(children),
    img: (props: any) => getImgComponent(props),
  });

  const getParagraphComponent = (props: any) => {
    const { children, className, align, style: incomingStyle, node, ...rest } = props || {};
    let resolvedAlign: any = undefined;

    if (incomingStyle && typeof incomingStyle === 'object' && (incomingStyle as any).textAlign) {
      resolvedAlign = (incomingStyle as any).textAlign;
    }
    const styleAttr = (node && (node as any).properties && (node as any).properties.style) as string | undefined;
    if (!resolvedAlign && typeof styleAttr === 'string') {
      const m = styleAttr.match(/text-align\s*:\s*(left|right|center|justify)/i);
      if (m) resolvedAlign = m[1];
    }
    if (!resolvedAlign && typeof align === 'string') resolvedAlign = align;
    if (!resolvedAlign && typeof className === 'string') {
      const cls = className.toLowerCase();
      if (/\b(text|align)[-_ ]?center\b/.test(cls)) resolvedAlign = 'center';
      else if (/\b(text|align)[-_ ]?right\b/.test(cls)) resolvedAlign = 'right';
      else if (/\b(text|align)[-_ ]?justify\b/.test(cls)) resolvedAlign = 'justify';
    }

    const baseStyle: React.CSSProperties = {
      fontSize: type === 'input' ? inputFontSize : fontSize,
      fontWeight: 500,
      letterSpacing: "0.015625rem",
      lineHeight: "1.375rem",
      margin: 0,
      marginBottom: noMargin ?? '0',
      paddingTop: paddingTop ?? '0',
      paddingBottom: paddingBottom ?? '0',
      color: type === 'input' ? style?.bot_styles?.input_message_font_color : style?.bot_styles?.output_message_font_color,
      fontFamily: style?.bot_styles?.general_font_style,
    };
    if (resolvedAlign) baseStyle.textAlign = resolvedAlign as any;

    return (
      <p {...rest} className={className} style={baseStyle}>
        {children}
      </p>
    );
  };

  const getTableComponent = (children: any) => (
    <table className="table">
      {children}
    </table>
  );

  const getTheadComponent = (children: any) => (
    <thead>{children}</thead>
  );

  const getThComponent = (children: any) => (
    <th>{children}</th>
  );

  const getTrComponent = (children: any) => (
    <tr>{children}</tr>
  );

  const getTdComponent = (children: any) => (
    <td>{children}</td>
  );

  const getImgComponent = (props: any) => {
    const { src, alt, title, width, height, style: incomingStyle = {} } = props || {};
    if (!isSafeUrl(src)) {
      return null;
    }
    const widthFromStyle = typeof incomingStyle?.width === 'string' ? incomingStyle.width : undefined;
    const heightFromStyle = typeof incomingStyle?.height === 'string' ? incomingStyle.height : undefined;
    const resolvedWidth = widthFromStyle || (typeof width === 'string' ? width : undefined);
    const resolvedHeight = heightFromStyle || (typeof height === 'string' ? height : undefined);
    return (
      <button
        style={{
          cursor: "pointer",
          background: 'none',
          border: 'none',
          padding: 0,
          display: 'inline-block',
          maxWidth: '100%'
        }}
        onClick={() => toggleModal(src)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleModal(src);
          }
        }}
        aria-label={`Click to preview ${alt || 'image'}`}
      >
        <img
          src={src}
          alt={alt}
          title={title}
          style={{ width: resolvedWidth || '33%', maxWidth: '100%', height: resolvedHeight || 'auto' }}
        />
      </button>
    );
  };

  return (
    <MarkdownContainer id="markDownContainer">
      {renderContent(preprocessChildren(children))}
      {renderModal()}
    </MarkdownContainer>
  );
}

export default WizrMarkDown;

